/* Financial Statements panel — iter-26 Phase C.
 *
 * 4 tabs: Income · Balance · Cash Flow · Equity
 * Period selector: Monthly (default), Quarterly, YTD
 * Manual journal entries inline-editable per statement.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TrendingUp, Scale, ArrowRightLeft, Coins, Plus, Trash2, Save,
  Loader2, RefreshCw, BookText, Calculator, PanelRightOpen, HeartHandshake,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import { PanelHeader } from './_shared';
import CogsAnalyzerPanel from './CogsAnalyzerPanel';
import TrialConversionsPanel from './TrialConversionsPanel';       // iter55z-audit round 9
import EditorAddonAbTestPanel from './EditorAddonAbTestPanel';     // iter55z-audit round 9
import MissedSearchPanel from './MissedSearchPanel';               // iter55z-audit round 9 (extras 7)
import EbitdaFinancialWidget from './EbitdaFinancialWidget';       // Feb-2026 — per-order EBITDA
import FinanceAdvisorSidebar from './FinanceAdvisorSidebar';       // Feb-2026 — AI CFO consultant sidebar
import ExecSummary from './ExecSummary';                            // Feb-2026 — compact square exec KPI
import LedgerPanel from './LedgerPanel';                            // Feb-2026 — General Ledger + Chart of Accounts
import ApprovalQueuePanel from './ApprovalQueuePanel';              // Feb-2026 — AP approval workflow
import EquityInjectionWizard from '@/components/admin/EquityInjectionWizard';
import { openConfirm }         from '@/components/admin/ConfirmDialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { BACKEND_URL } from '@/lib/api';
import CommunityPublishingFundPanel from './CommunityPublishingFundPanel';

const CATEGORY_LABEL_MAP = {
  equity_contributions: 'Equity Contributions',
  accounts_receivable:  'Accounts Receivable',
  accounts_payable:     'Accounts Payable',
  equipment:            'Equipment & PP&E',
  other_liabilities:    'Other Liabilities',
  advertising:          'Advertising',
  rent:                 'Rent',
  office:               'Office & Supplies',
  salaries:             'Salaries',
  other_opex:           'Other Operating Expenses',
  platform_dev:         'Platform Development',
  api_costs:            'API Vendor Costs',
  capex:                'Capital Expenditure',
  loans:                'Loans',
  owner_draws:          'Owner Draws',
  dividends:            'Dividends',
  owner_contributions:  'Owner Contributions',
  dividends_paid:       'Dividends Paid',
};

function GlAuditTrail({ period, http: httpClient }) {
  const [open,    setOpen]    = useState(false);
  const [rows,    setRows]    = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [balRes, incRes] = await Promise.all([
        httpClient.get('/admin/accounting/journal', { params: { statement: 'balance', period } }),
        httpClient.get('/admin/accounting/journal', { params: { statement: 'income',  period } }),
      ]);
      const all = [
        ...(balRes.data?.entries || []),
        ...(incRes.data?.entries || []),
      ].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      setRows(all);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [period, httpClient]);

  useEffect(() => {
    if (open && rows === null) load();
  }, [open, rows, load]);

  return (
    <div className="mt-3 rounded-md border border-border/60" data-testid="gl-audit-trail">
      <button
        className="flex w-full items-center gap-2 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground hover:bg-muted/30"
        onClick={() => setOpen(o => !o)}
        data-testid="gl-audit-trail-toggle"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        GL Contributions Audit Trail
        {rows !== null && <span className="ml-auto font-mono text-[10px]">{rows.length} entries</span>}
      </button>
      {open && (
        <div className="border-t border-border/60 px-3 pb-3">
          {loading ? (
            <div className="flex items-center gap-2 py-4 text-[12px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading entries…
            </div>
          ) : !rows?.length ? (
            <p className="py-3 text-center text-[12px] text-muted-foreground">No manual journal entries for this period.</p>
          ) : (
            <div className="mt-2 overflow-x-auto rounded-sm border border-border">
              <table className="w-full text-[11.5px]">
                <thead className="bg-panel-header/30 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Date</th>
                    <th className="px-2 py-1.5 text-left">Statement</th>
                    <th className="px-2 py-1.5 text-left">Category</th>
                    <th className="px-2 py-1.5 text-right">Amount</th>
                    <th className="px-2 py-1.5 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-border/40">
                      <td className="px-2 py-1 font-mono text-[10.5px] text-muted-foreground">
                        {(r.created_at || '').slice(0, 10)}
                      </td>
                      <td className="px-2 py-1 uppercase text-[10px] tracking-wider">
                        <span className={`inline-block rounded px-1 py-0.5 ${
                          r.statement === 'balance' ? 'bg-blue-500/15 text-blue-700 dark:text-blue-300' : 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
                        }`}>
                          {r.statement}
                        </span>
                      </td>
                      <td className="px-2 py-1">
                        {CATEGORY_LABEL_MAP[r.category] || r.category}
                      </td>
                      <td className="px-2 py-1 text-right font-mono-stat font-semibold">
                        {usdShort(r.amount_usd)}
                      </td>
                      <td className="px-2 py-1 text-muted-foreground max-w-[200px] truncate" title={r.notes}>
                        {r.notes || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const API = `${BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

function usdShort(v) {
  if (v == null) return '—';
  const sign = v < 0 ? '−' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function periodOptions() {
  const now = new Date();
  const Y = now.getUTCFullYear();
  const opts = [];
  for (let m = 1; m <= 12; m++) {
    const p = `${Y}-${String(m).padStart(2, '0')}`;
    opts.push({ value: p, label: `Monthly · ${p}` });
  }
  for (let q = 1; q <= 4; q++) opts.push({ value: `${Y}-Q${q}`, label: `Quarterly · ${Y} Q${q}` });
  opts.push({ value: `${Y}-YTD`, label: `Year-to-date · ${Y}` });
  opts.push({ value: `${Y}`, label: `Annual · ${Y}` });
  // Prior year YTD for comparison
  opts.push({ value: `${Y - 1}`, label: `Annual · ${Y - 1}` });
  return opts;
}

const TABS = [
  { id: 'ledger',   label: 'Ledger & COA',    icon: BookText },
  { id: 'ap-approvals', label: 'AP Approvals', icon: Scale },
  { id: 'income',   label: 'Income Statement', icon: TrendingUp },
  { id: 'balance',  label: 'Balance Sheet',    icon: Scale },
  { id: 'cashflow', label: 'Cash Flow',        icon: ArrowRightLeft },
  { id: 'equity',   label: 'Statement of Equity', icon: Coins },
  { id: 'cogs',     label: 'COGS Analyzer',   icon: Calculator },
  { id: 'fund',     label: 'Community Fund',  icon: HeartHandshake },
];

// iter55z-audit round 9 (extras) — Stripe Tax registration gap banner.
function StripeTaxRegistrationBanner() {
  const [state, setState] = useState(null);
  useEffect(() => {
    http.get('/admin/stripe-tax/registrations')
      .then((r) => setState(r.data))
      .catch(() => { /* silent — non-critical */ });
  }, []);
  if (!state) return null;
  const gaps = state.gaps || [];
  const gapCount = gaps.length;
  if (!state.configured) {
    return (
      <div data-testid="stripe-tax-registration-banner"
           className="rounded-md border border-border/60 bg-secondary/40 p-2 text-[12px] text-muted-foreground">
        <b>Stripe Tax registrations:</b> not readable from this pod.
        {' '}Manage registrations manually in the Stripe dashboard.
      </div>
    );
  }
  if (gapCount === 0) {
    return (
      <div data-testid="stripe-tax-registration-banner"
           className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-[12px] text-emerald-800 dark:text-emerald-300">
        <b>Stripe Tax:</b> registered in {state.registered_countries.length} country/countries.
        No customer country is out of coverage.
      </div>
    );
  }
  return (
    <div data-testid="stripe-tax-registration-banner"
         className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-[12px] text-destructive">
      <div className="flex items-center gap-2">
        <b>⚠ Stripe Tax gaps ({gapCount}):</b>
        {gaps.slice(0, 4).map((g) => (
          <span key={g.country}
                data-testid={`stripe-tax-gap-${g.country}`}
                className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[11px] font-semibold">
            {g.country} — {g.active_customers} active
          </span>
        ))}
        {gapCount > 4 && <span>+ {gapCount - 4} more</span>}
      </div>
      <div className="mt-1 opacity-90">
        Register in each country via Stripe → Tax → Registrations, or Stripe won't collect there.
      </div>
    </div>
  );
}

// iter55z-audit round 9 — Copyscape rate-watch dismissible banner.
function CopyscapeRateBanner() {
  const [state, setState] = useState(null);
  useEffect(() => {
    http.get('/admin/copyscape/rate-watch').then((r) => setState(r.data))
      .catch(() => { /* silent — non-critical */ });
  }, []);
  if (!state) return null;
  const over = state.alert_active;
  return (
    <div
      data-testid="copyscape-rate-banner"
      className={`rounded-md border px-3 py-2 text-[12px] ${
        over
          ? 'border-destructive/50 bg-destructive/10 text-destructive'
          : 'border-border/60 bg-secondary/40 text-muted-foreground'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="inline-flex h-5 items-center rounded-sm bg-current/10 px-1.5 text-[10.5px] font-bold uppercase tracking-wider">
          Copyscape
        </span>
        <span>
          Current rate: <b>${state.rate_usd.toFixed(3)}</b>/scan · Alert threshold:{' '}
          <b>${state.threshold_usd.toFixed(3)}</b>
          {state.last_probe_at && (
            <> · last checked {new Date(state.last_probe_at).toLocaleDateString()}</>
          )}
        </span>
        {over && (
          <span className="ml-auto font-semibold" data-testid="copyscape-rate-alert">
            ⚠ Above threshold — Pro+ / Premier margins at risk. Trim tier caps or reprice.
          </span>
        )}
      </div>
    </div>
  );
}

function StatementRow({ label, amount, indent = 0, strong = false, divider = false }) {
  return (
    <div className={`grid grid-cols-2 gap-4 ${divider ? 'border-t border-border pt-2' : ''}`} data-testid={`fin-row-${label.toLowerCase().replace(/\W/g, '-')}`}>
      <div className={`text-[12.5px] ${strong ? 'font-semibold text-foreground' : 'text-foreground/85'}`} style={{ paddingLeft: indent * 14 }}>
        {label}
      </div>
      <div className={`text-right font-mono-stat text-[12.5px] ${strong ? 'font-semibold text-foreground' : ''}`}>
        {usdShort(amount)}
      </div>
    </div>
  );
}

function JournalEditor({ statement, period, categories, labels, onChanged }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ category: categories[0], amount_usd: '', notes: '' });
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const r = await http.get('/admin/accounting/journal', { params: { statement, period } });
    setRows(r.data.entries || []);
  }, [statement, period]);

  useEffect(() => { reload(); }, [reload]);

  const add = async () => {
    const amt = parseFloat(form.amount_usd);
    if (!form.category || Number.isNaN(amt)) return toast.error('Pick a category and enter an amount.');
    setBusy(true);
    try {
      await http.post('/admin/accounting/journal', {
        statement, period, category: form.category, amount_usd: amt, notes: form.notes,
      });
      setForm({ category: categories[0], amount_usd: '', notes: '' });
      await reload();
      onChanged?.();
      window.dispatchEvent(new CustomEvent('finance:updated', { detail: { source: 'statement-journal' } }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!(await openConfirm({
      title: 'Delete this entry?',
      description: 'The row is removed from the financial statement. This cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'destructive',
    }))) return;
    await http.delete(`/admin/accounting/journal/${id}`);
    await reload();
    onChanged?.();
    window.dispatchEvent(new CustomEvent('finance:updated', { detail: { source: 'statement-journal-delete' } }));
  };

  return (
    <div className="mt-4 rounded-md border border-border bg-card p-3" data-testid={`fin-journal-${statement}`}>
      <div className="mb-2 inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">
        <BookText className="h-3 w-3" /> Manual entries · {period}
      </div>
      <div className="flex flex-wrap items-end gap-1.5">
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid={`fin-journal-${statement}-category`}
                className="h-7 rounded-sm border border-border bg-background px-2 text-[12px] outline-none focus:border-primary">
          {categories.map((c) => <option key={c} value={c}>{labels[c] || c}</option>)}
        </select>
        <input type="number" step="0.01" value={form.amount_usd}
                onChange={(e) => setForm({ ...form, amount_usd: e.target.value })}
                placeholder="Amount (USD)"
                data-testid={`fin-journal-${statement}-amount`}
                className="h-7 w-36 rounded-sm border border-border bg-background px-2 text-[12px] font-mono-stat outline-none focus:border-primary" />
        <input type="text" value={form.notes} maxLength={500}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Notes (e.g. invoice #, vendor)"
                data-testid={`fin-journal-${statement}-notes`}
                className="h-7 flex-1 rounded-sm border border-border bg-background px-2 text-[12px] outline-none focus:border-primary" />
        <button onClick={add} disabled={busy} data-testid={`fin-journal-${statement}-add`}
                className="inline-flex h-7 items-center gap-1 rounded-sm bg-primary px-2 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Add entry
        </button>
      </div>
      {rows.length > 0 && (
        <div className="mt-3 overflow-x-auto rounded-sm border border-border">
          <table className="w-full text-[11.5px]">
            <thead className="bg-panel-header/30 text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-2 py-1 text-left">Category</th>
                <th className="px-2 py-1 text-right">Amount</th>
                <th className="px-2 py-1 text-left">Notes</th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border/60">
                  <td className="px-2 py-1">{labels[r.category] || r.category}</td>
                  <td className="px-2 py-1 text-right font-mono-stat">{usdShort(r.amount_usd)}</td>
                  <td className="px-2 py-1 text-muted-foreground">{r.notes}</td>
                  <td className="px-2 py-1 text-right">
                    <button onClick={() => remove(r.id)} className="rounded-sm p-0.5 text-muted-foreground hover:bg-secondary hover:text-warning">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function FinancialStatementsPanel({ initialTab = 'income' } = {}) {
  const [period, setPeriod] = useState(`${new Date().getUTCFullYear()}-YTD`);
  const [report, setReport] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const [legacyPremier, setLegacyPremier] = useState(null);
  const [summaryOpen,    setSummaryOpen]    = useState(false);
  const [injectionOpen,  setInjectionOpen]  = useState(false);
  const opts = useMemo(() => periodOptions(), []);

  // Sync the tab when the parent switches this instance to a different
  // top-level Finance tab (e.g. Ledger → Balance Sheet).
  useEffect(() => { setTab(initialTab); }, [initialTab]);

  const load = useCallback(async () => {
    try {
      const r = await http.get('/admin/accounting/financials', { params: { period } });
      setReport(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load report'); }
  }, [period]);

  const loadLegacy = useCallback(async () => {
    try {
      const r = await http.get('/admin/financials/legacy-premier-count');
      setLegacyPremier(r.data);
    } catch { /* silent — non-critical */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const refresh = () => load();
    window.addEventListener('finance:updated', refresh);
    return () => window.removeEventListener('finance:updated', refresh);
  }, [load]);
  useEffect(() => { loadLegacy(); }, [loadLegacy]);
  useEffect(() => {
    const shortcut = (event) => {
      if (event.altKey && event.key.toLowerCase() === 's') {
        event.preventDefault(); setSummaryOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', shortcut);
    return () => window.removeEventListener('keydown', shortcut);
  }, []);

  const labels = report?.category_labels || {};
  const cats = report?.categories || {};

  return (
    <section data-testid="financials-panel" className={`flex min-h-0 flex-col ${['ledger', 'fund'].includes(tab) ? '' : '2xl:flex-row 2xl:gap-6'}`}>
      <div className="min-w-0 flex-1 2xl:min-w-[640px]">
        <PanelHeader title="Financial Statements"
                    subtitle="Income · Balance Sheet · Cash Flow · Statement of Changes in Equity. Stripe revenue + token packs + bookstore orders + COGS are pulled automatically; the rest are admin-entered with notes."
                    onRefresh={load} />

      {/* iter55z-audit — Grandfather-free migration banner. Surfaces
          the count of Premier subscribers still on the pre-bump price
          so ops can see the churn window. Silent when zero. */}
      {legacyPremier && legacyPremier.total_legacy > 0 && (
        <div
          data-testid="fin-legacy-premier-banner"
          className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-amber-400/50 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
        >
          <span className="inline-flex h-5 items-center rounded-sm bg-amber-500/20 px-1.5 text-[10.5px] font-bold uppercase tracking-wider">
            Legacy pricing
          </span>
          <span>
            <strong className="font-semibold">{legacyPremier.total_legacy}</strong> Premier subscriber{legacyPremier.total_legacy === 1 ? '' : 's'} still on
            {legacyPremier.monthly_legacy > 0 && (
              <> the old <b>${legacyPremier.old_monthly_usd}/mo</b>{legacyPremier.monthly_legacy > 1 ? ` (×${legacyPremier.monthly_legacy})` : ''}</>
            )}
            {legacyPremier.monthly_legacy > 0 && legacyPremier.yearly_legacy > 0 && ' or'}
            {legacyPremier.yearly_legacy > 0 && (
              <> the old <b>${legacyPremier.old_yearly_usd}/yr</b>{legacyPremier.yearly_legacy > 1 ? ` (×${legacyPremier.yearly_legacy})` : ''}</>
            )}
            {' '}rate. New signups use <b>${legacyPremier.new_monthly_usd}/mo</b> · <b>${legacyPremier.new_yearly_usd}/yr</b>. Existing subs stay on their contracted rate until they cancel or resubscribe.
          </span>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select value={period} onChange={(e) => setPeriod(e.target.value)}
                data-testid="financials-period-select"
                className="h-8 rounded-md border border-border bg-background px-2 text-[12px] outline-none focus:border-primary">
          {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button onClick={load} className="inline-flex h-8 items-center gap-1 rounded-md bg-secondary px-2 text-[11.5px] hover:bg-secondary/80"
                data-testid="financials-refresh">
          <RefreshCw className="h-3 w-3" /> Reload
        </button>
        <button type="button" onClick={() => setSummaryOpen(true)} className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-[11.5px] font-semibold shadow-sm hover:border-primary" data-testid="exec-summary-drawer-trigger"><PanelRightOpen className="h-3.5 w-3.5" /> Summary</button>
        <div className="flex flex-wrap gap-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
                    data-testid={`financials-tab-${t.id}`}
                    className={`inline-flex h-8 items-center gap-1 rounded-md px-3 text-[11.5px] font-semibold ${
                      tab === t.id ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'
                    }`}>
              <t.icon className="h-3 w-3" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'fund' ? (
        <div className="mt-4"><CommunityPublishingFundPanel /></div>
      ) : tab === 'cogs' ? (
        <div className="mt-4 space-y-4">
          <EbitdaFinancialWidget />
          <CogsAnalyzerPanel />
          {/* iter55z-audit round 9 — Copyscape rate watch banner,
              trial → paid conversion widget, and Editor add-on A/B
              price test surface all stacked under the COGS analyzer.
              iter55z-audit round 9 (extras) — Stripe Tax registration
              gap banner added at the top of the stack. */}
          <StripeTaxRegistrationBanner />
          <CopyscapeRateBanner />
          <TrialConversionsPanel />
          <EditorAddonAbTestPanel />
          {/* iter55z-audit round 9 (extras 7) — Missed-search backlog
              at the bottom of the stack. The 20 most-searched-and-empty
              queries surface as future FAQ / catalog gaps. */}
          <MissedSearchPanel />
        </div>
      ) : !report ? (
        <div className="mt-4 flex h-32 items-center justify-center text-[12px] text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {tab === 'ledger' && <LedgerPanel />}
          {tab === 'ap-approvals' && <ApprovalQueuePanel />}

          {tab === 'income' && (
            <div className="mt-4 rounded-md border border-border bg-card p-4" data-testid="financials-income">
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">Income Statement · {period}</div>
              <div className="mt-3 space-y-1">
                <StatementRow label="Revenue" strong amount={report.income.total_revenue} />
                <StatementRow indent={1} label="Token packs"        amount={report.income.revenue.token_packs} />
                <StatementRow indent={1} label="Subscriptions"      amount={report.income.revenue.subscriptions} />
                <StatementRow indent={1} label="Bookstore — Books"  amount={report.income.revenue.bookstore_books} />
                <StatementRow indent={1} label="Bookstore — Bundles" amount={report.income.revenue.bookstore_bundles} />
                <StatementRow indent={1} label="Weekly Maven"        amount={report.income.revenue.weekly_maven} />
                <StatementRow label="Cost of Goods Sold (COGS)" amount={-report.income.cogs} divider />
                <StatementRow label="Gross Profit" strong amount={report.income.gross_profit} divider />
                <StatementRow label="Operating Expenses" amount={-report.income.total_opex} divider />
                {Object.entries(report.income.operating_expenses).map(([k, v]) => (
                  <StatementRow key={k} indent={1} label={labels[k] || k} amount={-v} />
                ))}
                <StatementRow label="Operating Income" strong amount={report.income.operating_income} divider />
                <StatementRow label="Net Income" strong amount={report.income.net_income} divider />
              </div>
              <JournalEditor statement="income" period={period} categories={cats.income || []} labels={labels} onChanged={load} />
            </div>
          )}

          {tab === 'balance' && (
            <div className="mt-4 rounded-md border border-border bg-card p-4" data-testid="financials-balance">
              <div className="flex items-center justify-between">
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">Balance Sheet · as of {period}</div>
                <div className="flex items-center gap-2">
                  <div className={`text-[10.5px] font-semibold ${report.balance_sheet.balanced ? 'text-success' : 'text-warning'}`}>
                    {report.balance_sheet.balanced ? '✓ Balanced' : '⚠ Out of balance'}
                  </div>
                  <button
                    data-testid="equity-injection-wizard-btn"
                    onClick={() => setInjectionOpen(true)}
                    className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-[11px] font-semibold shadow-sm hover:border-primary hover:text-primary transition-colors"
                  >
                    <ArrowRightLeft className="h-3 w-3" /> Equity Injection
                  </button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <div className="text-[12px] font-semibold mb-1 uppercase tracking-wider text-muted-foreground">Assets</div>
                  <StatementRow label="Cash"                amount={report.balance_sheet.assets.current.cash} />
                  <StatementRow label="Accounts Receivable" amount={report.balance_sheet.assets.current.accounts_receivable} />
                  <StatementRow label="Inventory & Prepaids" amount={report.balance_sheet.assets.current.inventory_prepaids} />
                  <StatementRow label="Total Current"  strong amount={report.balance_sheet.assets.total_current} divider />
                  <StatementRow label="Equipment / PP&E"    amount={report.balance_sheet.assets.noncurrent?.equipment ?? report.balance_sheet.assets.non_current?.equipment} />
                  <StatementRow label="Total Non-current" strong amount={report.balance_sheet.assets.total_non_current} divider />
                  <StatementRow label="Total Assets"  strong amount={report.balance_sheet.assets.total} divider />
                  {/* GL detail — shows exactly what ledger entries contribute */}
                  {report.balance_sheet.gl_detail && (
                    <div className="mt-3 pt-2 border-t border-dashed border-border/60 space-y-0.5">
                      <p className="text-[9.5px] uppercase tracking-widest text-muted-foreground mb-1">GL Contributions</p>
                      <StatementRow indent={1} label="Cash In (GL debit)"  amount={report.balance_sheet.gl_detail.cash_in}  />
                      <StatementRow indent={1} label="Cash Out (GL credit)" amount={-report.balance_sheet.gl_detail.cash_out} />
                      <StatementRow indent={1} label="Equipment (GL net)"  amount={report.balance_sheet.gl_detail.equipment} />
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-[12px] font-semibold mb-1 uppercase tracking-wider text-muted-foreground">Liabilities & Equity</div>
                  <StatementRow label="Accounts Payable"    amount={report.balance_sheet.liabilities.accounts_payable} />
                  <StatementRow label="Loans"               amount={report.balance_sheet.liabilities.loans} />
                  <StatementRow label="Other Liabilities"   amount={report.balance_sheet.liabilities.other_liabilities} />
                  <StatementRow label="Total Liabilities" strong amount={report.balance_sheet.liabilities.total} divider />
                  <StatementRow label="Equity Contributions" amount={report.balance_sheet.equity.contributions} />
                  <StatementRow label="Retained Earnings"    amount={report.balance_sheet.equity.retained_earnings} />
                  <StatementRow label="Owner Draws"          amount={-report.balance_sheet.equity.owner_draws} />
                  <StatementRow label="Dividends"            amount={-report.balance_sheet.equity.dividends} />
                  <StatementRow label="Total Equity"  strong amount={report.balance_sheet.equity.total} divider />
                </div>
              </div>
              <JournalEditor statement="balance" period={period} categories={cats.balance || []} labels={labels} onChanged={load} />
              {/* GL Entry Audit Trail — collapsible list of all manual
                  journal entries that feed this balance sheet */}
              <GlAuditTrail period={period} http={http} />
            </div>
          )}

          {tab === 'cashflow' && (
            <div className="mt-4 rounded-md border border-border bg-card p-4" data-testid="financials-cashflow">
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">Cash Flow Statement · {period}</div>
              <div className="mt-3 space-y-1">
                <StatementRow label="OPERATING ACTIVITIES" strong amount={null} />
                <StatementRow indent={1} label="Net Income"           amount={report.cash_flow.operating.net_income} />
                <StatementRow label="Net Cash · Operating" strong amount={report.cash_flow.operating.total} divider />
                <StatementRow label="INVESTING ACTIVITIES" strong amount={null} divider />
                <StatementRow indent={1} label="Capital Expenditure"  amount={report.cash_flow.investing.capex} />
                <StatementRow label="Net Cash · Investing" strong amount={report.cash_flow.investing.total} divider />
                <StatementRow label="FINANCING ACTIVITIES" strong amount={null} divider />
                <StatementRow indent={1} label="Loans"                amount={report.cash_flow.financing.loans} />
                <StatementRow indent={1} label="Owner Contributions"  amount={report.cash_flow.financing.owner_contributions} />
                <StatementRow indent={1} label="Owner Draws"          amount={report.cash_flow.financing.owner_draws} />
                <StatementRow indent={1} label="Dividends"            amount={report.cash_flow.financing.dividends} />
                <StatementRow label="Net Cash · Financing" strong amount={report.cash_flow.financing.total} divider />
                <StatementRow label="Net Change in Cash" strong amount={report.cash_flow.net_change_in_cash} divider />
              </div>
              <JournalEditor statement="cashflow" period={period} categories={cats.cashflow || []} labels={labels} onChanged={load} />
            </div>
          )}

          {tab === 'equity' && (
            <div className="mt-4 rounded-md border border-border bg-card p-4" data-testid="financials-equity">
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">Statement of Changes in Equity · {period}</div>
              <div className="mt-3 space-y-1">
                <StatementRow label="Owner Contributions"  amount={report.equity_statement.owner_contributions} />
                <StatementRow label="Net Income"           amount={report.equity_statement.net_income} />
                <StatementRow label="Dividends Paid"       amount={report.equity_statement.dividends_paid} />
                <StatementRow label="Owner Draws"          amount={report.equity_statement.owner_draws} />
                <StatementRow label="Net Change in Equity" strong amount={report.equity_statement.net_change_in_equity} divider />
              </div>
              <JournalEditor statement="equity" period={period} categories={cats.equity || []} labels={labels} onChanged={load} />
            </div>
          )}
        </>
      )}
      </div>

      {!['ledger', 'fund'].includes(tab) && <div className="mt-4 space-y-3 2xl:mt-0 2xl:w-[360px] 2xl:shrink-0" data-testid="financials-advisor-slot">
        <div className="2xl:sticky 2xl:top-3 2xl:space-y-3 2xl:max-h-[calc(100vh-6rem)]">
          <FinanceAdvisorSidebar period={period} />
        </div>
      </div>}
      <Sheet open={summaryOpen} onOpenChange={setSummaryOpen}>
        <SheetContent side="right" className="w-[620px] max-w-[94vw] overflow-y-auto border-l border-border bg-background p-5 sm:max-w-[94vw]" overlayTestId="exec-summary-backdrop" closeTestId="exec-summary-drawer-close" data-testid="exec-summary-drawer">
          <SheetHeader className="border-b border-border pb-4 pr-8">
            <SheetTitle className="font-serif text-2xl">Executive Summary</SheetTitle>
            <SheetDescription>Current financial position and operating performance. Press Alt+S to toggle.</SheetDescription>
          </SheetHeader>
          <div className="pt-5"><ExecSummary drawer /></div>
        </SheetContent>
      </Sheet>

      {/* Equity Injection Wizard — mounted here so it overlays the full panel */}
      <EquityInjectionWizard
        open={injectionOpen}
        onClose={() => setInjectionOpen(false)}
        onPosted={() => { setInjectionOpen(false); load(); }}
      />
    </section>
  );
}
